import { customInterpolation, preventOneWordInLastLine } from '@lemonade-hq/cdk';
import type { WizardComponentAdapterProps } from '@lemonade-hq/chat-wizard-client-base';
import { DIAMOND_SPECIAL_CHAR, DIAMOND_WITH_SPARKS_SPECIAL_CHAR, interpolate } from '@lemonade-hq/uniclient-chat';
import type { InterpolableTextProps, UniclientAction } from '@lemonade-hq/uniclient-chat';
import { Flex, IsomorphicTooltip, linkButton, spacing, Text } from '@lemonade-hq/uniclient-theme';
import { lazy, Suspense, useCallback, useMemo } from 'react';
import * as styles from './InterpolableText.css';

const Diamond = lazy(
  async () => await import('../adapters/components/Diamond/Diamond').then(m => ({ default: m.Diamond })),
);

const DiamondWithSparks = lazy(
  async () =>
    await import('../adapters/components/DiamondWithSparks/DiamondWithSparks').then(m => ({
      default: m.DiamondWithSparks,
    })),
);

export interface InterpolableTextComponentProps {
  readonly text: InterpolableTextProps;
  readonly onAction?: (action: UniclientAction) => Promise<unknown>;
  readonly answers?: WizardComponentAdapterProps['answers'];
  readonly dangerous?: boolean;
  readonly preventDefault?: boolean;
  readonly variant?: 'inherit' | 'inverted';
}

const ComplexInterpolableText: React.FC<
  InterpolableTextComponentProps & { readonly text: Exclude<InterpolableTextProps, string> }
> = ({ text: { actions, text, tooltips }, onAction, answers = {}, dangerous, preventDefault, variant }) => {
  const onActionMemoized = useCallback(
    (event: React.MouseEvent<HTMLElement>, value: UniclientAction) => {
      if (preventDefault) {
        event.preventDefault();
      }

      void onAction?.(value);
    },
    [onAction, preventDefault],
  );

  const actionInterpolations = useMemo(() => {
    if (actions == null) return {};

    return Object.entries(actions).reduce(
      (hash, [key, value]) => ({
        ...hash,
        [key]: (interpolatedText: string) => {
          // NOTE: display is inline-block in order for ::first-letter selector to work well (since it doesn't work on `inline` elements)
          return (
            <span
              className={linkButton({ ...(variant != null && { variant }) })}
              onClick={e => onActionMemoized(e, value)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onActionMemoized(e as unknown as React.MouseEvent<HTMLElement>, value);
                }
              }}
              role="button"
              style={{ display: 'inline-block' }}
              tabIndex={0}
            >
              {interpolate(interpolatedText, answers)}
            </span>
          );
        },
      }),
      {},
    );
  }, [actions, answers, onActionMemoized, variant]);

  const tooltipInterpolations = useMemo(() => {
    if (tooltips == null) return {};

    return Object.entries(tooltips).reduce(
      (hash, [key, value]) => ({
        ...hash,
        [key]: (interpolatedText: string) => {
          const tooltipContent = typeof value === 'string' ? value : value.content;
          const tooltipMobileContent =
            typeof value === 'string'
              ? undefined
              : {
                  title: value.title,
                  content: value.content,
                };

          return (
            <IsomorphicTooltip
              content={tooltipContent}
              mobileContent={
                tooltipMobileContent != null ? (
                  <Flex flexDirection="column" gap={spacing.s04}>
                    <Text type="h5">{tooltipMobileContent.title}</Text>
                    <Text type="bodyMedium">{preventOneWordInLastLine(tooltipMobileContent.content)}</Text>
                  </Flex>
                ) : undefined
              }
            >
              <span
                className={linkButton({ ...(variant != null && { variant }), disableHover: true })}
                style={{ display: 'inline-block', cursor: 'unset' }}
              >
                {interpolate(interpolatedText, answers)}
              </span>
            </IsomorphicTooltip>
          );
        },
      }),
      {},
    );
  }, [tooltips, answers, variant]);

  const placeHolderInterpolations = useMemo(() => {
    return {
      [DIAMOND_SPECIAL_CHAR]: () => (
        <span className={styles.diamondSpan} data-testid="diamond-icon">
          <Suspense fallback={null}>
            <Diamond height={spacing.s24} width={spacing.s24} />
          </Suspense>
        </span>
      ),
      [DIAMOND_WITH_SPARKS_SPECIAL_CHAR]: () => (
        <span className={styles.diamondWithSparksSpanWrapper}>
          <span className={styles.diamondWithSparksSpan} data-testid="diamond-with-sparks-icon">
            <Suspense fallback={null}>
              <DiamondWithSparks height={styles.SPARKS_SIZE} width={styles.SPARKS_SIZE} />
            </Suspense>
          </span>
        </span>
      ),
    };
  }, []);

  return customInterpolation(
    text,
    { ...actionInterpolations, ...tooltipInterpolations, ...placeHolderInterpolations },
    { dangerous },
  );
};

export const InterpolableText: React.FC<InterpolableTextComponentProps> = ({
  text: interpolationText,
  onAction,
  answers = {},
  dangerous,
  preventDefault = true,
  variant,
}) => {
  if (typeof interpolationText === 'string') {
    const resultText = preventOneWordInLastLine(interpolate(interpolationText, answers));
    return dangerous ? <span dangerouslySetInnerHTML={{ __html: resultText }}></span> : <span>{resultText}</span>;
  }

  return (
    <ComplexInterpolableText
      answers={answers}
      dangerous={dangerous}
      onAction={onAction}
      preventDefault={preventDefault}
      text={interpolationText}
      variant={variant}
    />
  );
};
